"use client";

import type { ReactNode } from "react";
import { RegistrationFormPreview } from "./registration-form-preview";
import type { FormField } from "@/lib/types/form-fields";
import {
  formatSchedule,
  formatSessionLine,
  type OfferingSession,
} from "@/lib/types/sessions";
import { formatSpan, formatSpanEnds, spansDays } from "@/lib/format-date";
import { DEFAULT_TIMEZONE, tzHintLabel } from "@/lib/timezone";
import { cn } from "@/lib/utils";
import { safeHttpUrl } from "@/lib/url";
import {
  HeroCarousel,
  type CarouselImage,
} from "@/components/public/hero-carousel";
import type { OfferingImage } from "@/lib/types/images";
import { themeCssVars, type OfferingTheme } from "@/lib/types/theme";

export type OfferingPreviewData = {
  title: string;
  subtitle: string | null;
  hero_image_url: string | null;
  hero_focus_x?: number;
  hero_focus_y?: number;
  hero_attribution?: { name: string; href: string } | null;
  /** Gallery, in display order. When present (even with one entry) it drives
   *  the hero: 1 image renders like the classic hero, 2+ become a carousel.
   *  Absent → fall back to the flat hero_* fields above. */
  images?: OfferingImage[];
  description: string | null;
  description_heading: string | null;
  facilitator_name: string | null;
  facilitator_photo_url: string | null;
  logistics: string | null;
  logistics_heading: string | null;
  starts_at: string | null;
  /** Optional end timestamp for single-session offerings. Multi-session
   *  offerings drive end times via the `sessions` array. */
  ends_at?: string | null;
  /** Sessions for multi-day / recurring offerings. When length ≤ 1, the
   *  preview falls back to the original Date + Time tiles using starts_at. */
  sessions?: OfferingSession[];
  location_type: "physical" | "online" | "hybrid";
  location_address: string | null;
  /** Reveal-on-completion fields. Visible only on the confirmation page,
   *  not in the public hero. Carried in the type so the editor's right pane
   *  can pass them straight through. */
  location_online_url?: string | null;
  thank_you_message?: string | null;
  reveal_what_to_bring_url?: string | null;
  redirect_url?: string | null;
  form_fields?: FormField[];
  /** New as of the row-based canvas redesign — surfaced in the editor only
   *  for now; public-page badges (sold-out, approval-pending) are a follow-up. */
  capacity?: number | null;
  require_approval?: boolean;
  /** IANA timezone for date/time rendering. Stable across server and client
   *  to avoid SSR hydration mismatches. Defaults to "Africa/Johannesburg"
   *  if absent. */
  timezone?: string | null;
  /** Surface preset + accent colour. Absent / empty → house oatmeal. */
  theme?: OfferingTheme | null;
};

/**
 * Renders the public offering page exactly as attendees will see it.
 * Used both inside the editor's right pane and on /[slug] (kept in sync by hand).
 */
export function OfferingPreview({
  o,
  formSlot,
  embed,
}: {
  o: OfferingPreviewData;
  /** Optional override for the form region. The editor's right pane passes
   *  the disabled preview; the public page passes a real RegistrationForm. */
  formSlot?: ReactNode;
  /** When true (embed mode), strip the platform footer so the page sits
   *  cleanly inside a host site's iframe. */
  embed?: boolean;
}) {
  const tz = o.timezone;
  const startsAt = o.starts_at;
  const endsAt = o.ends_at ?? null;
  const sessions = o.sessions ?? [];
  const isMultiSession = sessions.length > 1;
  // One continuous block that crosses days — a retreat, a conference. The
  // Date/Time tiles become a date range plus the arrival and departure times.
  const isSpan = !!startsAt && spansDays(startsAt, endsAt, tz);
  // For online/hybrid events attendees may join from anywhere, so the bare
  // wall-clock time is ambiguous — name the zone under it. In-person events
  // read cleaner without it. The label is measured against every moment on
  // the page, so a schedule that straddles a clock change names the city
  // without quoting an offset that would be wrong for half of it.
  const showTzHint = o.location_type !== "physical";
  const tzHint = showTzHint
    ? tzHintLabel(tz || DEFAULT_TIMEZONE, [
        // Each pair is continuous, so it's sampled through the middle too.
        [startsAt, endsAt],
        ...sessions.map((s) => [s.starts_at, s.ends_at] as const),
      ])
    : null;

  // Only ever emit http(s) URLs into style/img — defangs legacy rows.
  const heroImageUrl = safeHttpUrl(o.hero_image_url);
  const facilitatorPhotoUrl = safeHttpUrl(o.facilitator_photo_url);

  // Gallery-first: sanitise every entry, drop anything non-http(s). Rows
  // without gallery entries fall back to the flat hero_* fields.
  const galleryImages: CarouselImage[] = (o.images ?? [])
    .map((img) => {
      const url = safeHttpUrl(img.url);
      return url
        ? {
            url,
            focus_x: img.focus_x ?? 0.5,
            focus_y: img.focus_y ?? 0.5,
            attribution: img.attribution ?? null,
          }
        : null;
    })
    .filter((img): img is CarouselImage => img !== null);
  if (galleryImages.length === 0 && heroImageUrl) {
    galleryImages.push({
      url: heroImageUrl,
      focus_x: o.hero_focus_x ?? 0.5,
      focus_y: o.hero_focus_y ?? 0.5,
      attribution: o.hero_attribution ?? null,
    });
  }

  return (
    <article className="bg-background" style={themeCssVars(o.theme)}>
      <div className="mx-auto max-w-2xl px-6 py-10">
        {galleryImages.length > 0 ? (
          <HeroCarousel images={galleryImages} className="mb-8" />
        ) : (
          <div
            className="mb-8 aspect-[16/9] w-full rounded-2xl bg-gradient-to-br from-[oklch(0.82_0.05_60)] via-[oklch(0.72_0.10_45)] to-[oklch(0.55_0.12_30)]"
            aria-hidden
          />
        )}

        <h1 className="font-serif text-3xl tracking-tight md:text-5xl">
          {o.title || "Untitled offering"}
        </h1>
        {o.subtitle && (
          <p className="mt-3 text-lg text-muted-foreground">{o.subtitle}</p>
        )}

        {isMultiSession ? (
          <dl className={FACTS_CLASS}>
            <div>
              <dt className={LABEL_CLASS}>When</dt>
              <dd className="mt-1 font-serif text-base">
                {formatSchedule(sessions, tz)}
                <TzHint hint={tzHint} />
              </dd>
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                  See all {sessions.length} sessions
                </summary>
                <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                  {sessions.map((s, i) => (
                    <li key={s.id ?? i}>
                      <span className="font-mono text-[11px] tabular-nums">
                        {String(i + 1).padStart(2, "0")}
                      </span>{" "}
                      {formatSessionLine(s, tz)}
                    </li>
                  ))}
                </ul>
              </details>
            </div>
            <Fact
              label="Location"
              value={
                o.location_type === "online"
                  ? "Online — link revealed on registration"
                  : o.location_address || "Location TBA"
              }
            />
          </dl>
        ) : (
          <dl className={FACTS_CLASS}>
            <div>
              <dt className={LABEL_CLASS}>When</dt>
              {!startsAt ? (
                <dd className="mt-1 font-serif text-base">Date TBA</dd>
              ) : isSpan ? (
                // Two ends of one stay, each a complete moment. See
                // ScheduleRail for why this isn't a date cell and a time cell.
                <ScheduleRail
                  {...formatSpanEnds(startsAt, endsAt!, tz)}
                  hint={tzHint}
                />
              ) : (
                <dd className="mt-1 font-serif text-base">
                  {formatSpan(startsAt, endsAt, tz)}
                  <TzHint hint={tzHint} />
                </dd>
              )}
            </div>
            <Fact
              label="Location"
              value={
                o.location_type === "online"
                  ? "Online — link revealed on registration"
                  : o.location_address || "Location TBA"
              }
            />
          </dl>
        )}

        {(o.description || o.description_heading) && (
          <section className="mt-8">
            {o.description_heading && (
              <h2 className="font-serif text-xl">{o.description_heading}</h2>
            )}
            {o.description && (
              <p
                className={cn(
                  "whitespace-pre-wrap text-base leading-relaxed",
                  o.description_heading && "mt-2"
                )}
              >
                {o.description}
              </p>
            )}
          </section>
        )}

        {(o.facilitator_name || facilitatorPhotoUrl) && (
          <div className="mt-10 flex items-center gap-4 rounded-xl border bg-card p-5">
            {facilitatorPhotoUrl ? (
              <img
                src={facilitatorPhotoUrl}
                alt={o.facilitator_name ?? ""}
                className="size-14 rounded-full object-cover"
              />
            ) : (
              <div
                className="size-14 rounded-full bg-gradient-to-br from-[oklch(0.82_0.05_60)] to-[oklch(0.55_0.12_30)]"
                aria-hidden
              />
            )}
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                Held by
              </p>
              <p className="font-serif text-lg">
                {o.facilitator_name || "Facilitator name"}
              </p>
            </div>
          </div>
        )}

        {o.logistics && (
          <section className="mt-10">
            <h2 className="font-serif text-xl">
              {o.logistics_heading || "What to know"}
            </h2>
            <p className="mt-2 whitespace-pre-wrap text-base leading-relaxed text-muted-foreground">
              {o.logistics}
            </p>
          </section>
        )}

        <div className="mt-10">
          {formSlot ?? <RegistrationFormPreview fields={o.form_fields ?? []} />}
        </div>

        {!embed && (
          <footer className="mt-16 text-xs text-muted-foreground">
            Made with{" "}
            <a href="/" className="underline-offset-4 hover:underline">
              Offerings
            </a>
            {" · "}
            <a href="/privacy" className="underline-offset-4 hover:underline">
              Privacy
            </a>
            {" · "}
            <a href="/terms" className="underline-offset-4 hover:underline">
              Terms
            </a>
          </footer>
        )}
      </div>
    </article>
  );
}

// The when/where block. "When" takes the wider track: for a residential
// event the location is one line and the timing is what's being decided.
const FACTS_CLASS =
  "mt-8 grid gap-5 border-y py-5 md:grid-cols-[1.5fr_1fr] md:gap-8";
const LABEL_CLASS =
  "text-xs uppercase tracking-[0.18em] text-muted-foreground";

/** Offset + city, under the value rather than parenthesised inside it. */
function TzHint({ hint }: { hint: string | null }) {
  if (!hint) return null;
  return <span className="block text-sm text-muted-foreground">{hint}</span>;
}

/**
 * A stay has one start and one end, not a date and a time. Splitting it
 * across two cells printed the weekday twice and left the reader to pair
 * the halves; this stacks the two ends as whole moments against a rail —
 * filled dot for the start, open ring for the end — which is also the
 * shape the organiser edits against in the Schedule panel.
 */
function ScheduleRail({
  start,
  end,
  hint,
}: {
  start: string;
  end: string;
  hint: string | null;
}) {
  return (
    <dd className="mt-2">
      <div className="flex gap-3.5">
        <div aria-hidden className="flex flex-col items-center py-1.5">
          <span className="size-[7px] shrink-0 rounded-full bg-foreground" />
          <span className="my-1 w-px flex-1 bg-border" />
          <span className="size-[7px] shrink-0 rounded-full border-[1.5px] border-foreground" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-3.5">
          {[
            { label: "Starts", value: start },
            { label: "Ends", value: end },
          ].map((row) => (
            <div key={row.label} className="flex flex-col">
              <span className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                {row.label}
              </span>
              <span className="font-serif text-base leading-snug">
                {row.value}
              </span>
            </div>
          ))}
        </div>
      </div>
      <TzHint hint={hint} />
    </dd>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className={LABEL_CLASS}>{label}</dt>
      <dd className="mt-1 font-serif text-base">{value}</dd>
    </div>
  );
}
